/**
 * AuthCounter — the shell every auth screen is built on.
 *
 * Rebuilt (2026-08) to `GardEn Auth.dc.html` from the Claude Design project.
 * Every colour, size, animation and offset below is transcribed from that file.
 *
 * ── WHAT THE DESIGN GAVE, AND WHAT IT DID NOT ───────────────────────────────
 *
 * The design is a single consumer screen with a client-side signin/signup
 * toggle. This product has THREE persona doors (consumer / seller / admin) on
 * three real routes, and the whole reason the previous shell existed was
 * SCRUM-145: a user could not tell which door had rejected them. So the design's
 * masthead — spinning square, wordmark, drawn separator, mono caps label — is
 * reproduced exactly, and its `{{ modeLabel }}` slot is where the DOOR NAME
 * goes. That is the design's own arrangement carrying the product's meaning:
 * "BUYER ENTRANCE" in the design becomes "ВХОД ДЛЯ ПОКУПАТЕЛЕЙ" /
 * "ВХОД ДЛЯ ПРОДАВЦОВ" / "СЛУЖЕБНЫЙ ВХОД" here, in the user's language.
 *
 * The 2px rule under the masthead is `#16453F` in the design. It stays tinted
 * per door, because that tint is the load-bearing part of the SCRUM-145 fix and
 * the design had no third door to colour. Consumer gets the design's own
 * `#16453F`, so the imported screen is pixel-identical on the door it drew.
 *
 * `data-persona` and `data-door` stay on `<main>` so a Playwright failure
 * screenshot is self-identifying.
 *
 * ── LANGUAGE CONTROL ────────────────────────────────────────────────────────
 * The design draws EN / RU / KG as a three-segment pill. That is reproduced as
 * three REAL buttons wired to the same i18n + ui-store change the shared
 * `LanguageSwitcher` performs — design-exact and functional, rather than a
 * dropdown dropped into a pill-shaped hole. Signup suppresses it: that form owns
 * an "interface language" field, and two controls for one setting on one screen
 * is worse than a small chrome difference.
 *
 * The theme toggle is gone from auth. The design ships one palette with no dark
 * variant, so these screens are light-only under it; see `auth-v2.css`.
 */
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { AuthPersona } from "@/features/auth/destination";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/i18n";
import { useUiStore } from "@/lib/ui-store";

import "@/features/auth/components/auth-v2.css";

export interface AuthCounterProps {
  /**
   * Which door this screen belongs to. Drives the masthead rule tint — the
   * whole point is that a user can never be silently at the wrong counter
   * (SCRUM-145).
   */
  door: AuthPersona;
  /** The door's name, in the user's language. Rendered as the design's caps slot. */
  doorName: string;
  /** Screen title (display voice). */
  title: string;
  /** One line under the title. Optional — the OAuth handoff has none. */
  lede?: ReactNode;
  /** Rendered directly under the lede, inside the heading block. */
  headerExtra?: ReactNode;
  /** Suppressed on signup, whose form owns the language field. */
  showLanguageSwitcher?: boolean;
  /** `data-testid` for `<main>`. Every existing id is preserved verbatim. */
  testId: string;
  /** Mirrors the `data-persona` attribute the login `<main>` has always had. */
  personaAttr?: AuthPersona;
  children: ReactNode;
}

/**
 * Bottom rule of the masthead. `#16453F` is the design's own value and belongs
 * to the consumer door; the other two are the door tints SCRUM-145 established.
 */
const DOOR_RULE: Record<AuthPersona, string> = {
  consumer: "#16453F",
  seller: "#C75A34",
  admin: "#4A473D",
};

/** Short codes for the design's language pill. `ky` prints as KG per the design. */
const LANG_CODE: Record<SupportedLanguage, string> = {
  ru: "RU",
  ky: "KG",
  en: "EN",
};
/** The design's pill order is EN · RU · KG. */
const LANG_ORDER: readonly SupportedLanguage[] = ["en", "ru", "ky"];

function LanguagePill({ disabled = false }: { disabled?: boolean }): ReactElement {
  const { t } = useTranslation("common");
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);

  return (
    <div
      role="group"
      aria-label={t("language.switchTo")}
      className="gav2-langpill"
      style={{
        display: "flex",
        border: "1px solid rgba(23,32,29,0.2)",
        borderRadius: 999,
        overflow: "hidden",
        fontSize: 12,
        fontWeight: 600,
        background: "#F7F2E9",
      }}
    >
      {LANG_ORDER.filter((l) => SUPPORTED_LANGUAGES.includes(l)).map((lng) => {
        const active = language === lng;
        return (
          <button
            key={lng}
            type="button"
            disabled={disabled}
            onClick={() => setLanguage(lng)}
            aria-pressed={active}
            aria-label={t("language.current", { language: t(`language.${lng}`) })}
            data-testid={`language-switcher-${lng}`}
            style={{
              padding: "7px 12px",
              border: "none",
              cursor: disabled ? "default" : "pointer",
              background: active ? "#16453F" : "transparent",
              color: active ? "#F0E9DA" : "#6E6A5E",
              font: "inherit",
            }}
          >
            {LANG_CODE[lng]}
          </button>
        );
      })}
    </div>
  );
}

export function AuthCounter({
  door,
  doorName,
  title,
  lede,
  headerExtra,
  showLanguageSwitcher = true,
  testId,
  personaAttr,
  children,
}: AuthCounterProps): ReactElement {
  return (
    <main
      className="gav2 gav2-shell"
      style={{ display: "flex", flexDirection: "column" }}
      data-testid={testId}
      data-persona={personaAttr}
      data-door={door}
    >
      <header style={{ position: "relative", background: "#F2ECDD", overflow: "hidden" }}>
        {/* The drifting shyrdak lattice — two nested diamonds per 140×120 tile. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='120'%3E%3Cpath d='M70 12 118 60 70 108 22 60Z' fill='none' stroke='%2316453F' stroke-opacity='0.14'/%3E%3Cpath d='M70 34 96 60 70 86 44 60Z' fill='none' stroke='%2316453F' stroke-opacity='0.08'/%3E%3C/svg%3E\")",
            animation: "ghdrift 34s linear infinite",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 22,
            right: "26%",
            width: 12,
            height: 12,
            background: "rgba(199,90,52,0.35)",
            animation: "ghfloat 5s ease-in-out infinite",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: 18,
            left: "22%",
            width: 9,
            height: 9,
            border: "1.5px solid rgba(22,69,63,0.35)",
            animation: "ghfloat 6.5s ease-in-out 0.8s infinite",
          }}
        />

        <div
          style={{
            position: "relative",
            maxWidth: 1280,
            margin: "0 auto",
            padding: "0 var(--gav2-shell-px)",
            minHeight: "var(--gav2-mast)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              background: "#F2ECDD",
              padding: "6px 14px",
              animation: "ghrise 0.7s cubic-bezier(0.2,0.7,0.2,1) both",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 11,
                height: 11,
                background: "#C75A34",
                display: "inline-block",
                animation: "ghspin 7s linear infinite",
              }}
            />
            <Link
              to="/"
              className="gav2-wordmark"
              style={{
                // A Latin logotype: the world's wordmark face, via its token.
                fontFamily: "var(--gd-wordmark)",
                fontWeight: 800,
                fontSize: "var(--gav2-wordmark)",
                color: "#16453F",
                letterSpacing: "-0.01em",
              }}
            >
              GardEn
            </Link>
            <span
              aria-hidden="true"
              style={{ width: 1, height: 26, background: "rgba(23,32,29,0.2)" }}
            />
            {/* The design's `{{ modeLabel }}` slot — the DOOR NAME here. */}
            <span
              style={{
                // IBM Plex Mono in every language, Kyrgyz included (design-world.css).
                fontFamily: "var(--gd-mono)",
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#4A473D",
              }}
            >
              {doorName}
            </span>
          </div>

          {showLanguageSwitcher ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: "#F2ECDD",
                padding: "6px 10px",
                animation: "ghrise 0.7s 0.15s cubic-bezier(0.2,0.7,0.2,1) both",
              }}
            >
              <LanguagePill />
            </div>
          ) : null}
        </div>

        <div
          aria-hidden="true"
          style={{
            position: "relative",
            height: 2,
            background: DOOR_RULE[door],
            transformOrigin: "0 50%",
            animation: "ghline 1.1s 0.2s cubic-bezier(0.2,0.7,0.2,1) both",
          }}
        />
      </header>

      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          padding:
            "var(--gav2-shell-pt) var(--gav2-shell-px) var(--gav2-shell-pb)",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 560,
            display: "flex",
            flexDirection: "column",
            gap: "var(--gav2-stack)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--gav2-title-gap)",
            }}
          >
            <h1
              style={{
                // Onest in every language: the design's Bricolage has no
                // Cyrillic (design-world.css).
                fontFamily: "var(--gd-display)",
                fontWeight: 800,
                fontSize: "var(--gav2-title)",
                letterSpacing: "-0.025em",
                margin: 0,
                lineHeight: 1.05,
              }}
            >
              {title}
            </h1>
            {lede !== undefined && lede !== null ? (
              <p
                style={{
                  fontSize: "var(--gav2-lede)",
                  color: "#4A473D",
                  margin: 0,
                }}
              >
                {lede}
              </p>
            ) : null}
            {headerExtra}
          </div>
          {children}
        </div>
      </div>
    </main>
  );
}

export default AuthCounter;
